import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/apiClient";
import { Lu } from "@/components/shared";
import { toast } from "sonner";

function InsightActions({ ins, idx, propId, L, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/properties/${propId}/ai-insights/${idx}/action`, { action });
      toast.success(action === "ticket" ? (L ? `تم إنشاء تذكرة صيانة (${r.data.ticket_id.slice(-6)})` : `Maintenance ticket created (${r.data.ticket_id.slice(-6)})`)
        : (L ? `تم تطبيق وضع التوفير على ${r.data.rooms_updated} غرفة` : `Eco scene applied to ${r.data.rooms_updated} rooms`));
      onDone();
    } catch { toast.error(L ? "فشل الإجراء" : "Action failed"); } finally { setBusy(false); }
  };
  if (ins.actioned) {
    return (
      <p data-testid={`ai-actioned-${idx}`} className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
        <Lu name="check-circle-2" className="w-3.5 h-3.5" />
        {ins.actioned.action === "ticket" ? (L ? "تم إنشاء تذكرة" : "Ticket created") : (L ? `تم تطبيق وضع التوفير (${ins.actioned.rooms_updated} غرفة)` : `Eco scene applied (${ins.actioned.rooms_updated} rooms)`)}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2 mt-2.5">
      <button data-testid={`ai-action-ticket-${idx}`} disabled={busy} onClick={() => run("ticket")}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-foreground/5 hover:bg-primary/10 border border-border hover:border-primary/40 text-foreground/80 disabled:opacity-50">
        <Lu name="ticket-plus" className="w-3.5 h-3.5" />{L ? "إنشاء تذكرة" : "Create ticket"}
      </button>
      {ins.type === "energy" && ins.rooms?.length > 0 && (
        <button data-testid={`ai-action-scene-${idx}`} disabled={busy} onClick={() => run("scene")}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 disabled:opacity-50">
          <Lu name="leaf" className="w-3.5 h-3.5" />{L ? "تطبيق وضع التوفير" : "Apply eco scene"}
        </button>
      )}
    </div>
  );
}

const SEV = { high: "#F43F5E", medium: "#F59E0B", low: "#10B981" };
const TYPE_ICON = { maintenance: "wrench", energy: "zap", occupancy: "users-round" };

export function AiInsights({ filter, compact = false }) {
  const { lang } = useLang();
  const { selected } = useProperty();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const L = lang === "ar";

  const load = (refresh = false) => {
    if (!selected) return;
    setBusy(true);
    api.get(`/properties/${selected.id}/ai-insights${refresh ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setBusy(false));
  };
  useEffect(() => { setData(null); load(); }, [selected?.id]); // eslint-disable-line

  const list = (data?.insights || []).filter((i) => !filter || filter.includes(i.type)).slice(0, compact ? 3 : 6);

  return (
    <div className="md-card p-5 relative overflow-hidden" data-testid="ai-insights-card">
      <div className="absolute -top-16 -end-16 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg md-gold-gradient flex items-center justify-center md-gold-glow">
            <Lu name="brain-circuit" className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
          </span>
          <div>
            <h3 className="font-semibold text-foreground leading-tight">{L ? "طبقة ذكاء مدارك" : "Madarek Intelligence Layer"}</h3>
            <p className="text-[11px] text-muted-foreground">{data?.source === "fallback" ? (L ? "وضع القواعد" : "Rule-based") : "Claude Sonnet 4.6"}{data?.generated_at && ` · ${new Date(data.generated_at).toLocaleTimeString(L ? "ar-SA" : "en-GB", { hour: "2-digit", minute: "2-digit" })}`}</p>
          </div>
        </div>
        <button data-testid="ai-refresh-btn" onClick={() => load(true)} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-border text-xs font-semibold text-foreground/80 disabled:opacity-50">
          <Lu name="refresh-cw" className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />{L ? "تحديث" : "Refresh"}
        </button>
      </div>

      {busy && !data && (
        <div className="space-y-3" data-testid="ai-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg bg-foreground/5 animate-pulse" />)}
          <p className="text-xs text-muted-foreground text-center">{L ? "يحلل Claude بيانات الغرف والطاقة..." : "Claude is analysing room & energy telemetry..."}</p>
        </div>
      )}

      {data && (
        <>
          <p className="text-sm text-foreground/80 mb-4 leading-relaxed" data-testid="ai-summary">{L ? data.summary_ar : data.summary_en}</p>
          <div className="space-y-2.5">
            {list.map((ins, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: L ? 10 : -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                data-testid={`ai-insight-${i}`} className="flex gap-3 p-3 rounded-lg bg-foreground/[0.03] border border-border">
                <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${SEV[ins.severity]}1f`, color: SEV[ins.severity] }}>
                  <Lu name={TYPE_ICON[ins.type] || "sparkles"} className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground truncate">{L ? ins.title_ar : ins.title_en}</p>
                    {ins.impact_sar_month > 0 && <span className="text-xs font-mono text-emerald-500 shrink-0">+{ins.impact_sar_month.toLocaleString()} {L ? "ر.س/شهر" : "SAR/mo"}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{L ? ins.detail_ar : ins.detail_en}</p>
                  {ins.rooms?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ins.rooms.slice(0, 6).map((r) => <span key={r} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-foreground/5 text-foreground/70">{r}</span>)}
                    </div>
                  )}
                  <InsightActions ins={ins} idx={data.insights.indexOf(ins)} propId={selected?.id} L={L} onDone={() => load()} />
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
